import { useEffect, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from 'src/contexts/auth';

import { ModelOfCreditor, ModelOfCreditorMaquina, ModelOfCreditorTipoCombustivel, ModelOfFuelType, ModelOfTipoCombustivel } from '../../database';
import api from '../../services/api';

const initialValuesOfCreditor = {
	NOME: '',
	CPF: '',
	CNPJ: '',
	CPF_CNPJ: '',
	ORGAO: '',
	BANCO: '',
	AGENCIA: '',
	CONTA: '',
	PESSOA: '',
};

const initialValuesOfCreditorMaquina = {
	DESCRICAO: '',
	MAQUINAID: '',
};

const initialValuesOfCreditorTipoCombustivel = {
	ID: '',
	CREDOR: '',
	TIPOCOMBUSTIVEL: '',
	DATA: '',
	VALOR: 0.0,
	AUTORIZADO: 1,
};

export const useListCreditorsController = () => {
	const [CreditorsData, setCreditorsData] = useState<ModelOfCreditor[]>([]);
	const [CreditorMaquinasData, setCreditorMaquinasData] = useState<ModelOfCreditorMaquina[]>([]);
	const [CreditorTipoCombustivelsData, setCreditorTipoCombustivelsData] = useState<ModelOfCreditorTipoCombustivel[]>([]);
	const [selectedCreditor, setSelectedCreditor] = useState<ModelOfCreditor>(initialValuesOfCreditor);
	const [selectedCreditorMaquina, setSelectedCreditorMaquina] = useState<ModelOfCreditorMaquina>(initialValuesOfCreditorMaquina);
	const [selectedCreditorTipoCombustivel, setSelectedCreditorTipoCombustivel] = useState<ModelOfCreditorTipoCombustivel>(initialValuesOfCreditorTipoCombustivel);
	const [loading, setLoading] = useState(false);
	const [loadingEdit, setLoadingEdit] = useState(false);
	const [loadingMaquina, setLoadingMaquina] = useState(false);
	const [loadingTipoCombustivel, setLoadingTipoCombustivel] = useState(false);
	const [editDialog, setEditDialog] = useState(false);
	const [maquinaDialog, setMaquinaDialog] = useState(false);
	const [tipoCombustivelDialog, setTipoCombustivelDialog] = useState(false);
	const [deleteDialog, setDeleteDialog] = useState(false);
	const [deleteMaquinaDialog, setDeleteMaquinaDialog] = useState(false);
	const [deleteTipoCombustivelDialog, setDeleteTipoCombustivelDialog] = useState(false);
	const [rows, setRows] = useState(0);
	const [maxData, setMaxData] = useState(false);
	const [loadingRows, setLoadingRows] = useState(false);
	const [fuelTypesList, setFuelTypesList] = useState<ModelOfFuelType[]>([]);

	const toast: any = useRef();
	const { userAgencyId } = useAuth();

	const validationSchemaCredor = Yup.object().shape({
		NOME: Yup.string().required('* Campo obrigatório'),
		BANCO: Yup.string().required('* Campo obrigatório'),
		AGENCIA: Yup.string().required('* Campo obrigatório'),
		CONTA: Yup.string().required('* Campo obrigatório'),
	});

	const validationSchemaCredorMaquina = Yup.object().shape({
		DESCRICAO: Yup.string().required('* Campo obrigatório'),
		MAQUINAID: Yup.string().required('* Campo obrigatório'),
	});

	const validationSchemaCredorTipoCombustivel = Yup.object().shape({
		TIPOCOMBUSTIVEL: Yup.string().required('* Campo obrigatório'),
		DATA: Yup.string().required('* Campo obrigatório'),
		VALOR: Yup.number().required('* Campo obrigatório').
			moreThan(0, 'O valor deve ser maior que zero')
			.typeError('O valor deve ser um número'),
	});

	const getCreditors = () => {
		if (rows === 0) {
			setLoading(true);
		} else {
			setLoadingRows(true);
		}
		api.post('creditors2', { ROW: rows })
			.then(response => {

				if (response.status === 204) {
					setMaxData(true);
					setLoading(false);
					setLoadingRows(false);
				} else {
					console.log("response ---", response)
					setCreditorsData(
						CreditorsData.concat(
							response.data.filter((item: any) => {
								return item.ORGAO === userAgencyId;
							}),
						),
					);
					setLoadingRows(false);
					setLoading(false);
				}
			})
			.catch((response: AxiosResponse) => {
				console.log('erro: ', response.status);
				setLoadingRows(false);
				setLoading(false);
			});
	};

	const getCreditorMaquinas = () => {
		setLoadingMaquina(true)
		api.get(`creditorsmaquinas/${selectedCreditor?.ID}`)
			.then(response => {
				setCreditorMaquinasData(response.data)
			})
			.catch((response: AxiosResponse) => {
				console.log('erro: ', response.status);
				setLoadingMaquina(false)
			});
		setLoadingMaquina(false)
	};

	const getCreditorTipoCombustivels = () => {
		setLoadingMaquina(true)
		api.get(`creditorstipocombustivels/${selectedCreditor?.ID}`)
			.then(response => {
				setCreditorTipoCombustivelsData(response.data)
			})
			.catch((response: AxiosResponse) => {
				console.log('erro: ', response.status);
				setLoadingTipoCombustivel(false)
			});
		setLoadingTipoCombustivel(false)
	};

	const removeCreditor = async (Creditor: ModelOfCreditor) => {
		setLoading(true);
		api.delete(`creditors/${Creditor.ID}`)
			.then(response => {
				const newList = CreditorsData.filter(creditor => creditor.ID !== response.data.ID);
				setCreditorsData(newList);
				setDeleteDialog(false);
				setLoading(false);
				toast.current.show({
					severity: 'success',
					summary: 'Remoção realizada',
					detail: 'O credor foi removida com sucesso!',
					life: 5000,
				});
			})
			.catch(() => {
				getCreditors();
				setDeleteDialog(false);
				setLoading(false);
				toast.current.show({
					severity: 'error',
					summary: 'Remoção não realizado',
					detail: 'Falha ao realizar operação!',
					life: 5000,
				});
			});
	};

	const removeCreditorMaquina = async (CreditorMaquina: ModelOfCreditorMaquina) => {
		setLoading(true);
		api.delete(`creditorsmaquina/${CreditorMaquina.ID}`)
			.then(response => {
				const newList = CreditorMaquinasData.filter(creditorMaquina => creditorMaquina.ID !== response.data.ID);
				setCreditorMaquinasData(newList);
				setDeleteMaquinaDialog(false);
				setLoading(false);
				toast.current.show({
					severity: 'success',
					summary: 'Remoção realizada',
					detail: 'A máquina foi removida com sucesso!',
					life: 5000,
				});
			})
			.catch(() => {
				getCreditorMaquinas();
				setDeleteMaquinaDialog(false);
				setLoading(false);
				toast.current.show({
					severity: 'error',
					summary: 'Remoção não realizado',
					detail: 'Falha ao realizar operação!',
					life: 5000,
				});
			});
	};

	const removeCreditorTipoCombustivel = async (CreditorTipoCombustivel: ModelOfCreditorTipoCombustivel) => {
		setLoading(true);
		api.delete(`creditorstipocombustivel/${CreditorTipoCombustivel.ID}`)
			.then(response => {
				const newList = CreditorTipoCombustivelsData.filter(creditorTipoCombustivel => creditorTipoCombustivel.ID !== response.data.ID);
				setCreditorTipoCombustivelsData(newList);
				setDeleteTipoCombustivelDialog(false);
				setLoading(false);
				toast.current.show({
					severity: 'success',
					summary: 'Remoção realizada',
					detail: 'A máquina foi removida com sucesso!',
					life: 5000,
				});
			})
			.catch(() => {
				getCreditorTipoCombustivels();
				setDeleteTipoCombustivelDialog(false);
				setLoading(false);
				toast.current.show({
					severity: 'error',
					summary: 'Remoção não realizado',
					detail: 'Falha ao realizar operação!',
					life: 5000,
				});
			});
	};

	const editCreditor = async (Creditor: ModelOfCreditor) => {
		setLoadingEdit(true);
		const editedCreditor: ModelOfCreditor = {
			...Creditor,
		};
		api.put(`creditors/${selectedCreditor?.ID}`, editedCreditor)
			.then(() => {
				getCreditors();
				setEditDialog(false);
				setLoadingEdit(false);
				toast.current.show({
					severity: 'success',
					summary: 'Edição realizada',
					detail: 'O credor foi editada com sucesso!',
					life: 5000,
				});
				setTimeout(() => {
					window.location.reload();
				}, 1500);
			})
			.catch(err => {
				getCreditors();
				setEditDialog(false);
				setLoadingEdit(false);
				toast.current.show({
					severity: 'error',
					summary: 'Edição não realizado',
					detail: 'Falha ao realizar operação! ' + err,
					life: 5000,
				});
			});
	};

	const editCreditorMaquina = async (CreditorMaquina: ModelOfCreditorMaquina) => {
		setLoading(true);

		const editedCreditorMaquina: ModelOfCreditorMaquina = {
			...CreditorMaquina,

		};
		editedCreditorMaquina.CREDOR = selectedCreditor.ID

		api.put(`creditorsmaquina/`, editedCreditorMaquina)
			.then(() => {
				getCreditorMaquinas();
				setLoading(false);
				toast.current.show({
					severity: 'success',
					summary: 'Edição realizada',
					detail: 'O credor foi editada com sucesso!',
					life: 5000,
				});
			})
			.catch(err => {
				getCreditorMaquinas();
				setLoading(false);;
				toast.current.show({
					severity: 'error',
					summary: 'Edição não realizado',
					detail: 'Falha ao realizar operação! ' + err,
					life: 5000,
				});
			});
		setLoading(false);
	};

	const editCreditorTipoCombustivel = async (CreditorTipoCombustivel: ModelOfCreditorTipoCombustivel) => {
		setLoading(true);

		const editedCreditorTipoCombustivel: ModelOfCreditorTipoCombustivel = {
			...CreditorTipoCombustivel,

		};
		editedCreditorTipoCombustivel.CREDOR = selectedCreditor.ID

		api.put(`creditorstipocombustivel/`, editedCreditorTipoCombustivel)
			.then(() => {
				getCreditorTipoCombustivels();
				setLoading(false);
				toast.current.show({
					severity: 'success',
					summary: 'Edição realizada',
					detail: 'O credor foi editada com sucesso!',
					life: 5000,
				});
			})
			.catch(err => {
				getCreditorTipoCombustivels();
				setLoading(false);;
				toast.current.show({
					severity: 'error',
					summary: 'Edição não realizado',
					detail: 'Falha ao realizar operação! ' + err,
					life: 5000,
				});
			});
		setLoading(false);
	};

	const openDeleteDialog = (Creditor: ModelOfCreditor) => {
		setDeleteDialog(true);
		setSelectedCreditor(Creditor);
	};

	const openDeleteMaquinaDialog = (CreditorMaquina: ModelOfCreditorMaquina) => {
		setDeleteMaquinaDialog(true);
		setSelectedCreditorMaquina(CreditorMaquina);
	};

	const openDeleteTipoCombustivelDialog = (CreditorTipoCombustivel: ModelOfCreditorTipoCombustivel) => {
		setDeleteTipoCombustivelDialog(true);
		setSelectedCreditorTipoCombustivel(CreditorTipoCombustivel);
	};

	const closeDeleteDialog = () => {
		setDeleteDialog(false);
	};

	const closeDeleteMaquinaDialog = () => {
		setDeleteMaquinaDialog(false);
	};

	const closeDeleteTipoCombustivelDialog = () => {
		setDeleteTipoCombustivelDialog(false);
	};

	const openEditDialog = (Creditor: ModelOfCreditor) => {
		setEditDialog(true);
		setSelectedCreditor(Creditor);
	};

	const openMaquinaDialog = (Creditor: ModelOfCreditor) => {
		setMaquinaDialog(true);
		setSelectedCreditor(Creditor);
	};

	const openTipoCombustivelDialog = (Creditor: ModelOfCreditor) => {
		setTipoCombustivelDialog(true);
		setSelectedCreditor(Creditor);
	};


	const closeEditDialog = () => {
		setEditDialog(false);
	};
	const closeMaquinaDialog = () => {
		console.log("passei aqui --- fechando o dialog")
		setMaquinaDialog(false);
	};

	const closeTipoCombustivelDialog = () => {
		console.log("passei aqui --- fechando o dialog")
		setTipoCombustivelDialog(false);
	};

	const formik = useFormik<ModelOfCreditor>({

		initialValues: {
			NOME: '',
			CPF_CNPJ: '',
			ORGAO: '',
			BANCO: '',
			AGENCIA: '',
			CONTA: '',
			PESSOA: '',
		},
		validationSchema: validationSchemaCredor,
		onSubmit: async (values) => {
			console.log("valores: ", values)
			await editCreditor(values)
		}

	});

	const formikMaquina = useFormik<ModelOfCreditorMaquina>({
		initialValues: {
			ID: '',
			MAQUINAID: '',
			DESCRICAO: '',
			CREDOR: ''
		},
		validationSchema: validationSchemaCredorMaquina,
		onSubmit: async values => await editCreditorMaquina(values),
	});

	const formikTipoCombustivel = useFormik<ModelOfCreditorTipoCombustivel>({
		initialValues: {
			ID: '',
			TIPOCOMBUSTIVEL: '',
			DATA: '',
			VALOR: 0.0,
			AUTORIZADO: 0.0,
			CREDOR: ''
		},
		validationSchema: validationSchemaCredorTipoCombustivel,
		onSubmit: async values => await editCreditorTipoCombustivel(values),
	});

	const getFueltypes = async () => {
		const response = await api.get(`/fuels/${userAgencyId}`);
		setFuelTypesList(response.data);
	};

	const fuels = fuelTypesList.map(fuels => {
		return { label: fuels.DESCRICAO, value: fuels.ID };
	});

	useEffect(() => {
		if (tipoCombustivelDialog)
			getFueltypes();
	}, [tipoCombustivelDialog])

	useEffect(() => {
		getCreditors();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rows]);

	return {
		getCreditorListController: {
			formik,
			formikMaquina,
			formikTipoCombustivel,
			CreditorsData,
			selectedCreditor,
			selectedCreditorMaquina,
			selectedCreditorTipoCombustivel,
			loading,
			deleteDialog,
			deleteMaquinaDialog,
			deleteTipoCombustivelDialog,
			editDialog,
			maquinaDialog,
			loadingEdit,
			loadingTipoCombustivel,
			toast,
			rows,
			maxData,
			loadingRows,
			tipoCombustivelDialog,
			CreditorTipoCombustivelsData,
			CreditorMaquinasData,
			getCreditorMaquinas,
			getCreditorTipoCombustivels,
			fuels,
		},
		handleCreditorListController: {
			removeCreditor,
			removeCreditorMaquina,
			removeCreditorTipoCombustivel,
			editCreditor,
			editCreditorMaquina,
			editCreditorTipoCombustivel,
			openDeleteDialog,
			openDeleteMaquinaDialog,
			openDeleteTipoCombustivelDialog,
			closeDeleteDialog,
			closeDeleteMaquinaDialog,
			closeDeleteTipoCombustivelDialog,
			openEditDialog,
			closeEditDialog,
			closeMaquinaDialog,
			closeTipoCombustivelDialog,
			setRows,
			openMaquinaDialog,
			openTipoCombustivelDialog,
			setCreditorMaquinasData,
			setCreditorTipoCombustivelsData,
		},
	};
};
